import { type DependencyList, useEffect, type RefObject } from 'react';

/** @param eventTarget 'document' | 'window' | React.RefObject<HTMLElement> */
function useEventListener<T extends keyof DocumentEventMap>(
    eventTarget: 'document',
    eventType: T,
    listener: (event: DocumentEventMap[T]) => void,
    dependencies?: DependencyList,
): void;

function useEventListener<T extends keyof WindowEventMap>(
    eventTarget: 'window',
    eventType: T,
    listener: (event: WindowEventMap[T]) => void,
    dependencies?: DependencyList,
): void;

function useEventListener<T extends Element, E extends keyof HTMLElementEventMap>(
    eventTarget: RefObject<T>,
    eventType: E,
    listener: (event: HTMLElementEventMap[E]) => void,
    dependencies?: DependencyList,
): void;

function useEventListener<T extends EventTarget>(
    eventTarget: T,
    eventType: string,
    listener: (event: Event) => void,
    dependencies?: DependencyList,
): void;

function useEventListener(
    eventTarget: 'document' | 'window' | RefObject<Element> | EventTarget,
    eventType:
        keyof DocumentEventMap | keyof WindowEventMap | keyof HTMLElementEventMap | (string & {}),
    listener: (event: any) => void,
    dependencies: DependencyList = [],
): void {
    useEffect(() => {
        const target: EventTarget | null =
            eventTarget === 'window'
                ? window
                : eventTarget === 'document'
                  ? document
                  : eventTarget instanceof EventTarget
                    ? eventTarget
                    : eventTarget.current;

        if (target === null) return;

        target.addEventListener(eventType, listener as EventListener);
        // eslint-disable-next-line consistent-return
        return () => target.removeEventListener(eventType, listener as EventListener);
    }, [eventType, listener, eventTarget, ...dependencies]);
}

export { useEventListener };
